import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiZoomIn, FiZoomOut, FiGrid, FiList, FiClock, FiCalendar } from 'react-icons/fi';

interface TimelineControlsProps {
  viewMode: 'vertical' | 'horizontal';
  onViewModeChange: (mode: 'vertical' | 'horizontal') => void;
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  groupBy: 'workflow' | 'status' | 'none';
  onGroupByChange: (group: 'workflow' | 'status' | 'none') => void;
}

export const TimelineControls: React.FC<TimelineControlsProps> = ({
  viewMode,
  onViewModeChange,
  zoomLevel,
  onZoomChange,
  groupBy,
  onGroupByChange
}) => {
  const { darkMode } = useTheme();
  
  return (
    <div className={`
      flex items-center gap-4 px-4 py-2 border-b
      ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}
    `}>
      {/* View Mode Toggle */}
      <div className="flex items-center gap-2">
        <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          View:
        </span>
        <div className="flex rounded-lg border overflow-hidden">
          <button
            onClick={() => onViewModeChange('vertical')}
            className={`
              flex items-center gap-1 px-3 py-1.5 text-sm transition-colors
              ${viewMode === 'vertical'
                ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100'
              }
            `}
          >
            <FiList className="w-4 h-4" />
            List
          </button>
          <button
            onClick={() => onViewModeChange('horizontal')}
            className={`
              flex items-center gap-1 px-3 py-1.5 text-sm transition-colors
              ${viewMode === 'horizontal'
                ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100'
              }
            `}
          >
            <FiGrid className="w-4 h-4" />
            Timeline
          </button>
        </div>
      </div>
      
      {/* Group By (only for horizontal) */}
      {viewMode === 'horizontal' && (
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Group:
          </span>
          <select
            value={groupBy}
            onChange={(e) => onGroupByChange(e.target.value as any)}
            className={`
              px-3 py-1.5 rounded-lg text-sm border focus:outline-none focus:ring-2
              ${darkMode 
                ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500' 
                : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500'
              }
            `}
          >
            <option value="none">No Grouping</option>
            <option value="workflow">By Workflow</option>
            <option value="status">By Status</option>
          </select>
        </div>
      )}
      
      {/* Zoom Controls (only for horizontal) */}
      {viewMode === 'horizontal' && (
        <div className="flex items-center gap-2 ml-auto">
          <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Zoom:
          </span>
          <button
            onClick={() => onZoomChange(Math.max(0.25, zoomLevel - 0.25))}
            className={`
              p-1.5 rounded-lg transition-colors
              ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}
            `}
            disabled={zoomLevel <= 0.25}
          >
            <FiZoomOut className="w-4 h-4" />
          </button>
          <span className={`text-sm w-12 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            onClick={() => onZoomChange(Math.min(4, zoomLevel + 0.25))}
            className={`
              p-1.5 rounded-lg transition-colors
              ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}
            `}
            disabled={zoomLevel >= 4}
          >
            <FiZoomIn className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
